import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

import { continuousPlayEnabled } from "@/lib/utils"

import type PersistentPlayerController from "./persistent_player_controller"
import type { NativeState } from "./persistent_player_controller"

interface BridgeMessage {
  event: string
  data: Record<string, unknown>
}

interface QueueEntry {
  itemId: string
  title: string
  creatorName: string
  durationSeconds: number
  imageUrl: string
  sourceUrl?: string | null
  itemUrl?: string | null
  capabilities?: Record<string, boolean>
}

// Native "player" bridge (#1460), re-homed from the retired container
// player onto the ONE global player. The native contract is unchanged —
// connect carries the queue payload, play/pause/seek command the native
// engine, stateChanged/positionChanged report back — but the web side
// now reads the live global queue (re-sent when the queue is replaced,
// via the playback-queue:change binding in the layout) instead of a
// static per-container payload.
export default class extends BridgeComponent {
  static component = "player"
  static outlets = ["persistent-player", "playback-queue"]

  declare readonly persistentPlayerOutlet: PersistentPlayerController
  declare readonly hasPersistentPlayerOutlet: boolean
  declare readonly playbackQueueOutlet: {
    items: QueueEntry[]
    currentItem: QueueEntry | undefined
    syncTo: (itemId: string, position: number) => void
  }
  declare readonly hasPlaybackQueueOutlet: boolean

  connect() {
    super.connect()
    this.sendQueue()
  }

  sendQueue() {
    this.send("connect", this.#queue(), (message: BridgeMessage) =>
      this.receive(message),
    )
  }

  // The queue goes first. Native cannot play a track it has not been sent.
  queueChanged(event: CustomEvent) {
    const detail = event.detail as {
      item?: { itemId: string }
      playIntent?: boolean
      replaced?: boolean
    } | null

    if (detail?.replaced) this.sendQueue()

    const item = detail?.item
    if (item && detail?.playIntent !== false) this.play(item.itemId)
  }

  play(itemId: string) {
    this.send("play", { version: this.#version(), itemId })
  }

  pause() {
    this.send("pause", { version: this.#version() })
  }

  seek(position: number) {
    this.send("seek", { version: this.#version(), position })
  }

  receive(message: BridgeMessage) {
    if (message.event === "stateChanged") {
      const state = message.data as unknown as NativeState
      if (this.hasPlaybackQueueOutlet)
        this.playbackQueueOutlet.syncTo(state.itemId, state.position)
      if (this.hasPersistentPlayerOutlet)
        this.persistentPlayerOutlet.applyNativeState(state)
    } else if (
      message.event === "positionChanged" &&
      this.hasPersistentPlayerOutlet
    ) {
      this.persistentPlayerOutlet.updateNativePosition(
        message.data.position as number,
      )
    }
  }

  #queue(): Record<string, unknown> {
    const items = this.hasPlaybackQueueOutlet
      ? this.playbackQueueOutlet.items
      : []
    const current = this.hasPlaybackQueueOutlet
      ? this.playbackQueueOutlet.currentItem
      : undefined
    return {
      version: this.#version(),
      autoPlay: continuousPlayEnabled(),
      currentItemId: current?.itemId ?? null,
      tracks: items.map((entry, index) => this.#track(entry, index)),
    }
  }

  #track(entry: QueueEntry, index: number): Record<string, unknown> {
    return {
      index,
      itemId: entry.itemId,
      title: entry.title,
      spaceName: entry.creatorName,
      capabilities: entry.capabilities ?? { audio: true, video: false },
      durationSeconds: entry.durationSeconds,
      playable: Boolean(entry.sourceUrl),
      sourceUrl: entry.sourceUrl ?? null,
      artworkUrl: entry.imageUrl || null,
      itemUrl: entry.itemUrl ?? null,
    }
  }

  #version(): number {
    return 1
  }
}
